import axios from 'axios';
import { catchError } from './common';
import qs from 'qs';
import { LANGUAGES } from '@/config/settings';

interface SettingsData {
  name_of_model: string;
  response_language: string;
}

export const getPrivateCollection = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/profile/self/collections/private/types`
    );
    return response.data;
  } catch (e: any) {
    console.error(e);
    catchError(e);
  }
};

export const getSources = async (
  myContentOnlyFlag: string,
  context?: string,
  searchTerm?: string,
  sortOrder?: string,
  limit?: string,
  offset?: string,
  type?: string,
  page?: number,
  pageSize?: number,
  retrieveOnlyProcessed?: string
): Promise<SourceResult> => {
  try {
    const data: any = { my_contents_only: myContentOnlyFlag };
    if (context) data['context'] = context;
    if (searchTerm) data['search_term'] = searchTerm;
    if (limit) data['limit'] = limit;
    if (offset) data['offset'] = offset;
    if (sortOrder) data['sort_order'] = sortOrder;
    if (type) data['type'] = type;
    if (retrieveOnlyProcessed) data['retrieve_only_processed_sources'] = retrieveOnlyProcessed;
    if (page !== undefined) data['page'] = page;
    if (pageSize !== undefined) data['page_size'] = pageSize;
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/content/sources?${qs.stringify(data)}`
    );
    return response.data;
  } catch (e: any) {
    console.error(e);
    catchError(e);
    //return not used, keep typescript happy
    let retObj: any;
    return retObj;
  }
};

export const deleteSource = async (source_id: string, type = ''): Promise<any> => {
  try {
    await axios.delete(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/content/source/${source_id}/${type}`
    );
    return { delete: 'success' };
  } catch (e: any) {
    console.log(e);
    catchError(e?.response?.data);
    return { delete: 'error' };
  }
};

export const getContentSignedUrl = async (
  collectionType: string,
  collectionProductLine: string,
  fileName: string,
  title: string,
  agentId?: string
): Promise<any> => {
  try {
    const data = {
      type: collectionType,
      product_line: collectionProductLine,
      id: fileName,
      title: title,
      agent_id: agentId,
      app_id: import.meta.env.VITE_ELYSIA_APP_ID,
    };
    const response = await axios.post(`${import.meta.env.VITE_API_DOMAIN}v2/ai/content/url`, data);
    return response.data.data;
  } catch (e: any) {
    console.log(e);
    throw e;
  }
};

export const uploadFileUsingUrl = async (url: string, file: File): Promise<any> => {
  try {
    const requestOptions = {
      method: 'PUT',
      body: file,
    };
    const response = await fetch(url, requestOptions);
    return response;
  } catch (e: any) {
    console.log(e);
    catchError(e);
    throw e;
  }
};

export const triggerIngestion = async (
  path: string,
  title: string,
  is_sync_flow = false,
  type?: string,
  collectionType?: string
): Promise<any> => {
  try {
    const data = {
      path: path,
      title: title,
      is_sync_flow: is_sync_flow,
      collectionType: collectionType,
    } as any;
    if (type) {
      data.type = type;
    }
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}v1/ai-ingestion/process-documents`,
      data
    );
    return response.data;
  } catch (e: any) {
    console.log(e);
    throw e;
  }
};

export const getModels = async () => {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_DOMAIN}v2/ai/settings/models`);
    return response?.data?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getDefaultValues = async (userId) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/settings/user/${userId}/default`
    );
    return response?.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const updateDefaultSettings = (userId: string, data: SettingsData): Promise<any> => {
  try {
    const response = axios.put(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/settings/user/${userId}/default`,
      data
    );
    return response;
  } catch (e: any) {
    console.error(e);
    catchError(e);
    return Promise.resolve([]);
  }
};

export const getAllLanguages = async () => {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_DOMAIN}v2/ai/settings/languages`);
    return response?.data?.data ?? LANGUAGES;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const downloadPresentations = async (presentationId: string) => {
  // to do : use one function for images,ppt,transcription
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}v2/ai/assets/presentations/${presentationId}`,
      {
        responseType: 'blob',
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};
